import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import type { LapComparisonChannel } from "@/types";
import { MAX_COMPARISON_CHANNELS } from "./comparisonMath";

interface ComparisonChannelPickerProps {
  channels: LapComparisonChannel[];
  selectedKeys: string[];
  onChange: (keys: string[]) => void;
  channelLabel: (channel: LapComparisonChannel) => string;
}

/**
 * Channel list for the comparison chart: shared channels first, then the
 * ones only one lap recorded (labelled with the lap that has them).
 */
export default function ComparisonChannelPicker({
  channels,
  selectedKeys,
  onChange,
  channelLabel,
}: ComparisonChannelPickerProps) {
  const { t } = useTranslation("pages");
  const selected = new Set(selectedKeys);
  const full = selectedKeys.length >= MAX_COMPARISON_CHANNELS;

  // The chart always shows at least one channel; the last one cannot be
  // unchecked (the URL would otherwise lose `ch` and snap back to the default).
  const toggle = (key: string) => {
    if (selected.has(key)) {
      if (selectedKeys.length > 1) onChange(selectedKeys.filter((k) => k !== key));
    } else if (!full) {
      onChange([...selectedKeys, key]);
    }
  };

  const groups: Array<{ title: string; items: LapComparisonChannel[] }> = [
    {
      title: t("lapComparison.channelsShared"),
      items: channels.filter((c) => c.availability === "both"),
    },
    {
      title: t("lapComparison.channelsSingle"),
      items: channels.filter((c) => c.availability !== "both"),
    },
  ].filter((group) => group.items.length > 0);

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("lapComparison.channels")}
        </h3>
        <span className="text-[11px] text-slate-600">
          {selectedKeys.length}/{MAX_COMPARISON_CHANNELS}
        </span>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
        {groups.map((group) => (
          <div key={group.title}>
            <div className="mb-1 text-[11px] text-slate-600">{group.title}</div>
            <ul className="space-y-0.5">
              {group.items.map((channel) => {
                const isSelected = selected.has(channel.key);
                const disabled = (!isSelected && full) || (isSelected && selectedKeys.length === 1);
                return (
                  <li key={channel.key}>
                    <button
                      type="button"
                      onClick={() => toggle(channel.key)}
                      disabled={disabled}
                      aria-pressed={isSelected}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors ${
                        isSelected
                          ? "bg-[#111827] text-slate-100"
                          : disabled
                            ? "cursor-not-allowed text-slate-700"
                            : "text-slate-400 hover:bg-[#0f172a] hover:text-slate-200"
                      }`}
                    >
                      <span
                        className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
                          isSelected ? "border-slate-300 bg-slate-200 text-[#080810]" : "border-slate-600"
                        }`}
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                      </span>
                      <span className="min-w-0 flex-1 truncate">
                        {channelLabel(channel)}
                        {channel.availability !== "both" && (
                          <span className="text-slate-500">
                            {" · "}
                            {channel.availability === "reference"
                              ? t("lapComparison.onlyReference")
                              : t("lapComparison.onlyComparison")}
                          </span>
                        )}
                      </span>
                      {channel.unit && (
                        <span className="shrink-0 text-[10px] text-slate-600">{channel.unit}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
